import { MAX_PAGE_SIZE } from '../types/common.js';

export type QueryInput = Record<string, unknown> | undefined;

let warnedAboutLimit = false;

/** @internal Test seam. */
export function __resetQueryWarnings(): void {
  warnedAboutLimit = false;
}

function serializeValue(value: unknown): string | null {
  if (value === undefined || value === null || value === '') return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null;
  }
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

/**
 * Build a query string from a params object.
 *
 * - drops `undefined` / `null` / `''`
 * - `Date` values become ISO strings
 * - arrays repeat the key
 * - `filters` is JSON-stringified — the backend runs `JSON.parse` on it
 * - `limit` is clamped to 50, because the backend caps it silently and a
 *   silently truncated page is the single most confusing SDK behaviour
 */
export function buildQuery(params: QueryInput): string {
  if (!params) return '';

  const search = new URLSearchParams();

  for (const [key, rawValue] of Object.entries(params)) {
    if (rawValue === undefined || rawValue === null) continue;

    if (key === 'limit') {
      const limit = Number(rawValue);
      if (!Number.isFinite(limit)) continue;
      if (limit > MAX_PAGE_SIZE) {
        if (!warnedAboutLimit) {
          warnedAboutLimit = true;
          console.warn(
            `[@gpmpay/sdk] limit=${String(limit)} exceeds the API maximum of ${String(MAX_PAGE_SIZE)}; ` +
              `requesting ${String(MAX_PAGE_SIZE)} instead. Paginate with \`page\`, or use listAll().`,
          );
        }
        search.append('limit', String(MAX_PAGE_SIZE));
      } else {
        search.append('limit', String(limit));
      }
      continue;
    }

    if (key === 'filters' && typeof rawValue === 'object') {
      search.append('filters', JSON.stringify(rawValue));
      continue;
    }

    if (Array.isArray(rawValue)) {
      for (const item of rawValue) {
        const serialized = serializeValue(item);
        if (serialized !== null) search.append(key, serialized);
      }
      continue;
    }

    const serialized = serializeValue(rawValue);
    if (serialized !== null) search.append(key, serialized);
  }

  const qs = search.toString();
  return qs === '' ? '' : `?${qs}`;
}

/** Normalize `string | Date` inputs for request bodies. */
export function toIsoString(value: string | Date | null | undefined): string | undefined {
  if (value === undefined || value === null) return undefined;
  return value instanceof Date ? value.toISOString() : value;
}
